import React from 'react';

import { ContentBlock } from 'components/modules/AutoruLayout/ContentBlock/ContentBlock';
import { CombinedInputs } from 'components/modules/Insurance/components/CombinedInputs/CombinedInputs';
import { DatePicker } from 'components/modules/Insurance/components/DatePicker/DatePicker';
import { FormWrapper } from 'components/modules/Insurance/components/FormWrapper/FormWrapper';
import { TextInputPreview } from 'components/modules/Insurance/components/TextInputPreview/TextInputPreview';
import {
  MAX_DATE,
  MIN_DATE,
  OUTPUT_CUSTOMER_DATE_FORMAT,
} from 'components/modules/Insurance/Insurance.constants';
import { VOID_FUNCTION } from 'constants/index';
import { InputMask } from 'utils/inputMask';

import { AddressInput } from '../AddressInput/AddressInput';
import { IssuerInput } from '../IssuerInput/IssuerInput';
import { ManualAddressForm } from '../ManualAddressForm/ManualAddressForm';

import { NAMES } from './Passport.constants';
import { usePassportPresenter } from './Passport.presenter';
import * as S from './Passport.styles';

export const Passport = ({ customer = {}, isOwnerForm }) => {
  const presenter = usePassportPresenter({ customer, isOwnerForm });

  return (
    <ContentBlock title="Паспорт">
      <FormWrapper>
        <CombinedInputs>
          <S.TextInputMasked
            disabled={presenter.disabled}
            error={presenter.errors[NAMES.PASSPORT_SERIES_AND_NUMBER]}
            label="Серия, номер"
            mask={InputMask.PASSPORT_SERIA_NUMBER}
            name={NAMES.PASSPORT_SERIES_AND_NUMBER}
            type="address"
            value={presenter.values[NAMES.PASSPORT_SERIES_AND_NUMBER]}
            onChange={presenter.onChange}
          />

          <DatePicker
            disabled={presenter.disabled}
            error={presenter.errors[NAMES.PASSPORT_DATE_ISSUE]}
            label="Дата выдачи"
            maxDate={MAX_DATE}
            minDate={MIN_DATE}
            name={NAMES.PASSPORT_DATE_ISSUE}
            outputFormat={OUTPUT_CUSTOMER_DATE_FORMAT}
            value={presenter.values[NAMES.PASSPORT_DATE_ISSUE]}
            onChange={presenter.onChange}
          />
        </CombinedInputs>

        <IssuerInput
          disabled={presenter.disabled}
          error={presenter.errors[NAMES.PASSPORT_ISSUER_CODE]}
          label="Код подразделения"
          mask={InputMask.PASSPORT_DEPARTMENT_CODE2}
          name={NAMES.PASSPORT_ISSUER_CODE}
          type="code"
          unmask={false}
          value={presenter.values[NAMES.PASSPORT_ISSUER_CODE]}
          onChange={presenter.onChange}
        />
      </FormWrapper>

      <IssuerInput
        disabled={presenter.disabled}
        error={presenter.errors[NAMES.PASSPORT_ISSUER]}
        label="Кем выдан"
        mask={InputMask.ALL_RUS}
        name={NAMES.PASSPORT_ISSUER}
        type="name"
        value={presenter.values[NAMES.PASSPORT_ISSUER]}
        onChange={presenter.onChange}
      />

      <S.TextInputMasked
        disabled={presenter.disabled}
        error={presenter.errors[NAMES.BIRTH_PLACE]}
        label="Место рождения"
        mask={InputMask.ALL_RUS_BIRTH_PLACE}
        name={NAMES.BIRTH_PLACE}
        value={presenter.values[NAMES.BIRTH_PLACE]}
        onChange={presenter.onChange}
      />

      {/* Инпут должен отображаться только если нет признака, что адрес был введен вручную ( на бэке "isRegistrationAddressFromDadata" ) */}
      {/* И так-же инпут не должен отображаться при ручном вводе адреса */}
      {presenter.values[NAMES.REGISTRATION_ADDRESS_FROM_DA_DATA] &&
        !presenter.manualRegistrationAddress.value && (
          <AddressInput
            disabled={presenter.disabled}
            error={presenter.errors[NAMES.REGISTRATION_ADDRESS]}
            label="Адрес регистрации"
            name={NAMES.REGISTRATION_ADDRESS}
            value={presenter.values[NAMES.REGISTRATION_ADDRESS]}
            onChange={presenter.onChange}
          />
        )}

      {/* Превью будет отображаться только если есть признак, что адрес введен вручную ( на бэке "isRegistrationAddressFromDadata" ) */}
      {/* И так-же инпут не должен отображаться при ручном вводе адреса */}
      {!presenter.values[NAMES.REGISTRATION_ADDRESS_FROM_DA_DATA] &&
        !presenter.manualRegistrationAddress.value && (
          <TextInputPreview
            editable={!presenter.disabled}
            error={presenter.errors[NAMES.REGISTRATION_ADDRESS]}
            label="Адрес регистрации"
            value={presenter.values[NAMES.REGISTRATION_ADDRESS]}
            onEdit={presenter.manualRegistrationAddress.onToggle}
          />
        )}

      <S.Checkbox
        disabled={presenter.disabled}
        name={NAMES.LIVING_ADDRESS_EQUALS}
        value={presenter.values[NAMES.LIVING_ADDRESS_EQUALS]}
        onChange={presenter.onChange}
      >
        Совпадает с адресом проживания
      </S.Checkbox>

      {/* Чекбокс должен отображаться только если нет признака, что адрес был введен вручную ( на бэке "isRegistrationAddressFromDadata" ) */}
      {presenter.values[NAMES.REGISTRATION_ADDRESS_FROM_DA_DATA] && (
        <S.Checkbox
          disabled={presenter.disabled}
          value={presenter.manualRegistrationAddress.value}
          onChange={presenter.manualRegistrationAddress.onToggle}
        >
          В справочнике нет адреса регистрации, как в паспорте
        </S.Checkbox>
      )}

      {presenter.manualRegistrationAddress.value && (
        <ManualAddressForm
          initData={presenter.values[NAMES.MANUAL_REGISTRATION_ADDRESS]}
          title="Адрес по паспорту"
          tooltip="Заполнили поля данными, выбранными из&nbsp;справочника. Измените параметры так, чтобы&nbsp;адрес регистрации совпадал с&nbsp;паспортом"
          onCancel={presenter.manualRegistrationAddress.onDisable}
          onSave={presenter.disabled ? VOID_FUNCTION : presenter.manualRegistrationAddress.onSave}
        />
      )}

      {!presenter.values[NAMES.LIVING_ADDRESS_EQUALS] && (
        <>
          {/* Инпут и чекбокс должны отображаться только если нет признака, что адрес был введен вручную ( на бэке "isLivingAddressFromDadata" ) */}
          {/* И так-же элементы не должны отображаться при ручном вводе адреса */}
          {presenter.values[NAMES.LIVING_ADDRESS_FROM_DA_DATA] &&
            !presenter.manualLivingAddress.value && (
              <>
                <AddressInput
                  disabled={presenter.disabled}
                  error={presenter.errors[NAMES.LIVING_ADDRESS]}
                  label="Адрес проживания"
                  name={NAMES.LIVING_ADDRESS}
                  value={presenter.values[NAMES.LIVING_ADDRESS]}
                  onChange={presenter.onChange}
                />

                <S.Checkbox
                  disabled={presenter.disabled}
                  value={presenter.manualLivingAddress.value}
                  onChange={presenter.manualLivingAddress.onToggle}
                >
                  В справочнике нет правильного адреса проживания
                </S.Checkbox>
              </>
            )}

          {/* Превью будет отображаться только если есть признак, что адрес введен вручную ( на бэке "isLivingAddressFromDadata" ) */}
          {/* И так-же инпут не должен отображаться при ручном вводе адреса */}
          {!presenter.values[NAMES.LIVING_ADDRESS_FROM_DA_DATA] &&
            !presenter.manualLivingAddress.value && (
              <TextInputPreview
                editable={!presenter.disabled}
                error={presenter.errors[NAMES.LIVING_ADDRESS]}
                label="Адрес проживания"
                value={presenter.values[NAMES.LIVING_ADDRESS]}
                onEdit={presenter.manualLivingAddress.onToggle}
              />
            )}

          {presenter.manualLivingAddress.value && (
            <ManualAddressForm
              initData={presenter.values[NAMES.MANUAL_LIVING_ADDRESS]}
              title="Адрес проживания"
              tooltip="Заполнили поля данными, выбранными из&nbsp;справочника. Измените параметры так, чтобы&nbsp;адрес проживания был верным"
              onCancel={presenter.manualLivingAddress.onDisable}
              onSave={presenter.disabled ? VOID_FUNCTION : presenter.manualLivingAddress.onSave}
            />
          )}
        </>
      )}

      <S.IndependentSwitcher
        disabled={presenter.disabled}
        label="Недавно изменился паспорт"
        name={NAMES.PASSPORT_CHANGED}
        value={presenter.values[NAMES.PASSPORT_CHANGED]}
        onChange={presenter.onChange}
      />

      {presenter.values[NAMES.PASSPORT_CHANGED] && (
        <FormWrapper>
          <CombinedInputs>
            <S.TextInputMasked
              disabled={presenter.disabled}
              error={presenter.errors[NAMES.PREVIOUS_PASSPORT_SERIES]}
              label="Серия предыдущего паспорта"
              mask={InputMask.PASSPORT_SERIES}
              name={NAMES.PREVIOUS_PASSPORT_SERIES}
              value={presenter.values[NAMES.PREVIOUS_PASSPORT_SERIES]}
              onChange={presenter.onChange}
            />
            <S.TextInputMasked
              disabled={presenter.disabled}
              error={presenter.errors[NAMES.PREVIOUS_PASSPORT_NUMBER]}
              label="Номер предыдущего паспорта"
              mask={InputMask.PASSPORT_NUMBER}
              name={NAMES.PREVIOUS_PASSPORT_NUMBER}
              value={presenter.values[NAMES.PREVIOUS_PASSPORT_NUMBER]}
              onChange={presenter.onChange}
            />
          </CombinedInputs>
          <DatePicker
            disabled={presenter.disabled}
            error={presenter.errors[NAMES.PREVIOUS_PASSPORT_DATE_ISSUE]}
            label="Дата выдачи предыдущего паспорта"
            maxDate={presenter.maxPreviousPassportDate}
            minDate={MIN_DATE}
            name={NAMES.PREVIOUS_PASSPORT_DATE_ISSUE}
            outputFormat={OUTPUT_CUSTOMER_DATE_FORMAT}
            value={presenter.values[NAMES.PREVIOUS_PASSPORT_DATE_ISSUE]}
            onChange={presenter.onChange}
          />
        </FormWrapper>
      )}
    </ContentBlock>
  );
};
